import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { List, X, InstagramLogo } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/concept', label: 'Concept' },
    { path: '/bar-mobile', label: 'Bar Mobile' },
    { path: '/studio-crea', label: 'Studio Créa' },
    { path: '/prestation-cocktail', label: 'Prestation Cocktail' },
    { path: '/location-partenaires', label: 'Location & Partenaires' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'bg-black/90 backdrop-blur-md py-4' : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="hover:opacity-70 transition-opacity flex items-center -ml-2 relative"
          >
            <div className="h-24 w-24 relative" style={{ 
              WebkitMaskImage: 'url(https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/e2epx8vp_logo%20studiobar.png)',
              WebkitMaskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskImage: 'url(https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/e2epx8vp_logo%20studiobar.png)',
              maskSize: 'contain',
              maskRepeat: 'no-repeat',
              maskPosition: 'center',
              backgroundColor: 'white'
            }} />
          </Link>

          {/* Desktop Navigation - Unified spacing */}
          <nav className="hidden lg:flex items-center">
            <div className="flex items-center gap-7">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                  className={`text-[11px] font-body uppercase tracking-[0.12em] transition-opacity whitespace-nowrap ${
                    location.pathname === link.path
                      ? 'text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            
            {/* Separator */}
            <div className="w-px h-4 bg-white/20 mx-6"></div>
            
            {/* Instagram Icon */}
            <a
              href="https://www.instagram.com/studiobar.paris/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram"
              className="text-white/60 hover:text-white transition-opacity flex items-center justify-center mr-6"
              title="@studiobar.paris"
            >
              <InstagramLogo size={18} weight="light" />
            </a>
            
            {/* CTA Button */}
            <Link
              to="/projet"
              data-testid="header-cta"
              className="btn-primary text-[10px] py-2.5 px-4 whitespace-nowrap"
            >
              Parlez-nous de votre projet
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2"
          >
            {isMobileMenuOpen ? <X size={28} weight="light" /> : <List size={28} weight="light" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] lg:hidden"
          >
            <div className="absolute inset-0 bg-black" />
            <nav className="relative h-full flex flex-col items-center justify-center gap-8 z-10">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                    className={`text-2xl font-display tracking-tight ${
                      location.pathname === link.path
                        ? 'text-white'
                        : 'text-white/60 hover:text-white'
                    } transition-colors`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              {/* Mobile Instagram Link */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <a
                  href="https://www.instagram.com/studiobar.paris/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-white/60 hover:text-white transition-colors"
                >
                  <InstagramLogo size={24} weight="light" />
                  <span className="text-lg">@studiobar.paris</span>
                </a>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-8"
              >
                <Link to="/projet" className="btn-primary">
                  Parlez-nous de votre projet
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
